// Sabit Rapor · 5160 — Demirbaş Son Durum (Raporlar → Sabit Raporlar).
//
// İki ekran (Sabit Rapor deseni):
//   1) AssetStatusCriteriaPage — kriter formu. Distribütör Kod ZORUNLU; Müşteri Kodu, Seri No
//      (operatörlü) ve Durum opsiyonel. "Raporu Göster" ayrı pencerede açar.
//   2) AssetStatusListPage — /api/detail-reports/asset-status'tan demirbaş (Asset) listesi + durum
//      dağılımı KPI'ları. Asset yalnız Customer'a bağlı olduğundan Distribütör Customer.Vendor
//      üzerinden çözülür; şemada Depo alanı yok → rapor mevcut alanlara uyarlandı. Durum
//      ACTIVE/FAULTY/INACTIVE/REMOVED → Aktif/Arızalı/Pasif/Hurda.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams} from 'react-router-dom';
import { authHeaders, exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { MultiSelect } from './reports/MultiSelect';

const ENDPOINT = '/api/detail-reports/asset-status';
const REPORT_CODE = '5160';
const REPORT_TITLE = 'Demirbaş Son Durum';

// ── Yardımcılar ──
function normalizeList(payload) { if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return []; }
const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k];
  return undefined; };
const optId = (o) => pick(o, 'id', 'Id');
const optCode = (o) => pick(o, 'textId', 'TextId', 'erpId', 'ErpId');
const optName = (o) => pick(o, 'name', 'Name', 'description', 'Description', 'title', 'Title');
const optLabel = (o) => { const code = optCode(o);
  const name = optName(o);
  if (code && name) return `${code} - ${name}`;
  return name || code || '—'; };
// Lookup listesini MultiSelect'in beklediği {value,label} şekline çevirir.
const asChoices = (arr) => (arr || []).map((o) => ({ value: String(optId(o)), label: optLabel(o) }));
const nfInt = new Intl.NumberFormat('tr-TR');
const fmtInt = (v) => nfInt.format(Number(v) || 0);

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'FAULTY', label: 'Arızalı' },
  { value: 'INACTIVE', label: 'Pasif' },
  { value: 'REMOVED', label: 'Hurda' },
];

function useLookups() { const [lk, setLk] = useState({ vendors: [], customers: [] });
  useEffect(() => { const headers = authHeaders();
    const load = (url) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then(normalizeList).catch(() => []);
    // Bağımsız yükle — Distribütör dropdown'ı ağır/askıda kalan Müşteri Select'ini beklemesin.
    load('/api/vendors/Select').then((vendors) => setLk((s) => ({ ...s, vendors })));
    load('/api/customers/Select').then((customers) => setLk((s) => ({ ...s, customers }))); }, []);
  return lk; }

/* ════════════════════════════ STYLES ════════════════════════════ */
const c = { page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  hTitle: { fontSize: 22, fontWeight: 800, color: 'var(--text-primary, #0f172a)', margin: 0 },
  hCrumb: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #64748b)', marginTop: 2 },
  card: { background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 18, boxShadow: 'var(--shadow-card)', padding: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardHeadIcon: { width: 40, height: 40, borderRadius: 12, background: 'var(--bg-input, #eef2ff)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent, #0073ea)' },
  cardTitle: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  pill: { fontSize: 12, fontWeight: 700, color: 'var(--accent, #0073ea)', background: 'var(--bg-input, #eef2ff)', padding: '3px 10px', borderRadius: 999 },
  toggleRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--text-secondary, #475569)' },
  req: { color: '#dc2626', marginLeft: 3 },
  input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13.5,
    width: '100%', boxSizing: 'border-box' },
  inputReq: { borderColor: '#fca5a5', background: 'rgba(220,38,38,0.03)' },
  sectionSep: { display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 4px',
    fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', color: 'var(--text-secondary, #94a3b8)' },
  sepLine: { flex: 1, height: 1, background: 'var(--border-color, #e5e7eb)' },
  btn: { padding: '11px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost: { background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)' },
  btnPrimary: { background: 'var(--accent-strong, #1e3a8a)', color: '#fff' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '44px 24px', textAlign: 'center' },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, background: 'var(--bg-input, #eef2f7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', marginBottom: 6 },
  okChip: { marginTop: 8, padding: '8px 16px', borderRadius: 10, background: 'rgba(22,163,74,0.10)', color: '#16a34a', fontWeight: 700, fontSize: 13 },
  warn: { marginTop: 8, padding: '8px 16px', borderRadius: 10, background: 'rgba(217,119,6,0.10)', color: '#b45309', fontWeight: 700, fontSize: 13 } };

function Toggle({ on, onChange }) { return (
    <button type="button" onClick={() => onChange(!on)}
      style={{ width: 46, height: 26, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 3,
        background: on ? 'var(--accent, #0073ea)' : 'var(--border-color, #cbd5e1)', display: 'flex',
        justifyContent: on ? 'flex-end' : 'flex-start', transition: 'all .15s' }}>
      <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'block' }} />
    </button>
  ); }
function Field({ label, required, children }) { return (
    <label style={c.field}>
      <span style={c.label}>{label}{required && <span style={c.req}>*</span>}</span>
      {children}
    </label>
  ); }
function Select({ value, onChange, options, placeholder = 'Tümü', required, invalid }) { return (
    <select style={{ ...c.input, ...(required && invalid ? c.inputReq : {}) }} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(o.value ?? optId(o))} value={String(o.value ?? optId(o))}>
          {o.label !== undefined ? o.label : optLabel(o)}
        </option>
      ))}
    </select>
  ); }

/* ═══════════════════════ 1) KRİTER EKRANI ═══════════════════════ */
export function AssetStatusCriteriaPage() { usePageMeta(REPORT_TITLE, "Sabit Raporlar › Demirbaş");
  const lk = useLookups();
  const [vendorId, setVendorId] = useState([]);
  const [customerId, setCustomerId] = useState([]);
  const [serialOp, setSerialOp] = useState('eq');
  const [serialNo, setSerialNo] = useState('');
  const [status, setStatus] = useState([]);
  const [showCriteria, setShowCriteria] = useState(false);

  const ready = !!vendorId.length;
  const selectedCount = [vendorId, customerId, serialNo, status].filter(Boolean).length;

  // Müşteri listesi seçili distribütöre göre süzülür (Customer.VendorId); etiket "kod - ünvan".
  const customerOptions = useMemo(() => { const list = vendorId
      ? lk.customers.filter((cu) => String(pick(cu, 'vendorId', 'VendorId')) === String(vendorId))
      : lk.customers;
    return list.map((cu) => ({ value: String(optId(cu)), label: optLabel(cu) })); }, [lk.customers, vendorId]);

  const clear = () => { setVendorId([]); setCustomerId([]); setSerialNo(''); setStatus([]); setSerialOp('eq'); };

  const showReport = () => { if (!ready) return;
    const p = new URLSearchParams();
    p.set('vendorId', vendorId);
    if (customerId.length) p.set('customerId', customerId.join(','));
    if (status.length) p.set('status', status.join(','));
    if (serialNo.trim()) { p.set('serialNo', serialNo.trim()); p.set('serialOp', serialOp); }
    if (showCriteria) p.set('showCriteria', '1');
    p.set('embed', 'true');
    const w = Math.min(1600, window.screen.availWidth - 40);
    const h = Math.min(940, window.screen.availHeight - 60);
    const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
    window.open(`/reports/asset-status-list?${p.toString()}`, 'demirbasSonDurum5160',
      `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`); };

  return (
    <div style={c.page}>

      <div style={c.card}>
        <div style={c.cardHead}>
          <div style={c.cardHeadLeft}>
            <span style={c.cardHeadIcon}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </span>
            <span style={c.cardTitle}>Kriterler</span>
            <span style={c.pill}>{selectedCount} alan seçili</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={{ ...c.btn, ...c.btnGhost }} onClick={clear}>✕ Filtreleri Temizle</button>
            <button type="button" style={{ ...c.btn, ...c.btnPrimary, opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }} onClick={showReport} disabled={!ready}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              Raporu Göster
            </button>
          </div>
        </div>

        <div style={c.toggleRow}>
          <Toggle on={showCriteria} onChange={setShowCriteria} />
          <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>Girilen kriterleri raporda göster</span>
        </div>

        <div style={c.grid}>
          <Field label="Distribütör Kod" required>
            <MultiSelect value={vendorId} onChange={(v) => { setVendorId(v); setCustomerId([]); }} options={asChoices(lk.vendors)} placeholder="Zorunlu alan" />
          </Field>
          <Field label="Müşteri">
            <MultiSelect value={customerId} onChange={setCustomerId} options={customerOptions} />
          </Field>
        </div>

        <div style={c.sectionSep}><span>SERİ NO &amp; DURUM</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="Operatör">
            <select style={c.input} value={serialOp} onChange={(e) => setSerialOp(e.target.value)}>
              <option value="eq">= Eşittir</option>
              <option value="contains">⊃ İçerir</option>
            </select>
          </Field>
          <Field label="Seri No">
            <input style={c.input} value={serialNo} onChange={(e) => setSerialNo(e.target.value)} placeholder="Seri numarası girin…" />
          </Field>
          <Field label="Durum">
            <MultiSelect value={status} onChange={setStatus} options={STATUS_OPTIONS} />
          </Field>
        </div>
      </div>

      <div style={c.card}>
        <div style={c.emptyState}>
          <span style={c.emptyIcon}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.3 7 12 12l8.7-5" /><path d="M12 22V12" /></svg>
          </span>
          <strong style={{ fontSize: 17, color: 'var(--text-primary, #0f172a)' }}>Rapor ayrı ekranda görüntülenir</strong>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary, #64748b)', maxWidth: 560 }}>
            <strong>Distribütör Kod</strong> zorunludur. Diğer alanlar (müşteri, seri no, durum) opsiyonel filtredir.
            Doldurup <strong>Raporu Göster</strong>’e basın — rapor tam ekran açılır, Excel/PDF olarak indirebilirsiniz.
          </span>
          {ready
            ? <div style={c.okChip}>✓ Zorunlu alan tamam · rapor hazır</div>
            : <div style={c.warn}>⚠ Eksik: Distribütör Kod</div>}
        </div>
      </div>
    </div>
  ); }

/* ═══════════════════════ 2) RAPOR EKRANI ═══════════════════════ */
// Kod rozeti ve durum rozeti hücreleri — ReportGrid `cell` render'ı olarak kullanılır.
const CodeChip = ({ v }) => (v ? <span style={l.codeChip}>{v}</span> : '—');
function StatusBadge({ v }) { const t = statusTone(v);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, background: t.bg, color: t.fg, fontWeight: 700, fontSize: 12 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.dot }} />{v ?? '—'}
    </span>
  ); }

const COLUMNS = [
  { key: 'distCode', label: 'Distribütör Kod', sticky: true, cell: (row) => <CodeChip v={row.distCode} /> },
  { key: 'distName', label: 'Distribütör Adı', bold: true },
  { key: 'status', label: 'Durum', cell: (row) => <StatusBadge v={row.status} /> },
  { key: 'customerCode', label: 'Müşteri Kodu', cell: (row) => <CodeChip v={row.customerCode} /> },
  { key: 'customerTitle', label: 'Müşteri Ünvanı' },
  { key: 'assetType', label: 'Demirbaş Tipi' },
  { key: 'brand', label: 'Marka' },
  { key: 'serialNo', label: 'Seri No' },
  { key: 'barcode', label: 'Barkod' },
  { key: 'quantity', label: 'Miktar', num: true, fmt: fmtInt },
];

// Kolon filtre/sıralama/gruplama/sayfalama ARTIK ReportGrid'in içinde (TanStack). Eski
// client-side rowMatches/pageWindow/PAGE_SIZES ve grup dropdown'ı kaldırıldı — grid yönetiyor.

const l = { wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-page, #f1f5f9)' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '14px 20px', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #e5e7eb)',
    position: 'sticky', top: 0, zIndex: 5 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10,
    border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-input, #f8fafc)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 800, fontSize: 13, cursor: 'pointer' },
  titleIcon: { width: 42, height: 42, borderRadius: 12, background: 'var(--accent-strong, #1e3a8a)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  subtitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #64748b)' },
  iconBtn: { width: 42, height: 42, borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  critBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', flexWrap: 'wrap',
    background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #eef2f7)', fontSize: 12.5 },
  critChip: { padding: '6px 12px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 700 },
  groupSel: { height: 34, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 700, fontSize: 13, padding: '0 8px', cursor: 'pointer' },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: '14px 20px 4px' },
  kpiCard: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14,
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)' },
  kpiIcon: (bg, fg) => ({ width: 40, height: 40, borderRadius: 11, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }),
  kpiLabel: { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary, #64748b)' },
  kpiValue: { fontSize: 20, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  tableScroll: { flex: 1, overflow: 'auto', padding: '10px 20px 20px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--bg-card, #fff)' },
  th: { position: 'sticky', top: 0, background: 'var(--bg-card, #f8fafc)', textAlign: 'left',
    padding: '11px 12px', fontWeight: 800, color: 'var(--text-secondary, #64748b)', fontSize: 11.5,
    borderBottom: '2px solid var(--border-color, #e5e7eb)', whiteSpace: 'nowrap', zIndex: 1 },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border-color, #eef2f7)', whiteSpace: 'nowrap', color: 'var(--text-primary, #0f172a)' },
  idx: { width: 34, textAlign: 'right', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
  groupRow: { background: 'var(--bg-input, #f1f5f9)', fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  empty: { padding: 60, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '12px 20px', background: 'var(--bg-card, #fff)', borderTop: '1px solid var(--border-color, #e5e7eb)', position: 'sticky', bottom: 0 },
  thInner: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between' },
  funnelBtn: (active) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0,
    borderRadius: 6, cursor: 'pointer', padding: 0,
    border: `1px solid ${active ? 'var(--accent, #0073ea)' : 'var(--border-color, #e5e7eb)'}`,
    background: active ? 'var(--accent, #0073ea)' : 'var(--bg-card, #fff)',
    color: active ? '#fff' : 'var(--text-secondary, #94a3b8)' }),
  codeChip: { padding: '3px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 12.5 },
  backdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  popover: { position: 'fixed', zIndex: 41, width: 250, padding: 12, borderRadius: 12,
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    boxShadow: '0 12px 32px rgba(15,23,42,0.18)', display: 'flex', flexDirection: 'column', gap: 10 },
  popTitle: { fontSize: 12, fontWeight: 800, color: 'var(--text-secondary, #64748b)' },
  popInput: { padding: '9px 10px', borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  popBtns: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  popBtn: (primary) => ({ padding: '8px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 12.5,
    background: primary ? 'var(--accent-strong, #1e3a8a)' : 'var(--bg-input, #f1f5f9)',
    color: primary ? '#fff' : 'var(--text-secondary, #475569)' }),
  pageBtn: (active, disabled) => ({ minWidth: 34, height: 34, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)',
    background: active ? 'var(--accent-strong, #1e3a8a)' : 'var(--bg-card, #fff)',
    color: active ? '#fff' : 'var(--text-primary, #0f172a)', fontWeight: 800, fontSize: 13,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, padding: '0 8px' }),
  pageSizeSel: { height: 34, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 700, fontSize: 13, padding: '0 8px', marginLeft: 8, cursor: 'pointer' } };

function statusTone(v) { if (v === 'Aktif') return { bg: 'rgba(22,163,74,0.10)', fg: '#16a34a', dot: '#16a34a' };
  if (v === 'Arızalı') return { bg: 'rgba(217,119,6,0.10)', fg: '#d97706', dot: '#d97706' };
  if (v === 'Pasif') return { bg: 'var(--bg-input, #f1f5f9)', fg: '#64748b', dot: '#94a3b8' };
  if (v === 'Hurda') return { bg: 'rgba(220,38,38,0.10)', fg: '#dc2626', dot: '#dc2626' };
  return { bg: 'var(--bg-input, #f1f5f9)', fg: '#94a3b8', dot: '#cbd5e1' }; }

function KpiCard({ label, value, bg, fg, icon }) { return (
    <div style={l.kpiCard}>
      <span style={l.kpiIcon(bg, fg)}>{icon}</span>
      <div><div style={l.kpiLabel}>{label}</div><div style={l.kpiValue}>{value}</div></div>
    </div>
  ); }

const hesc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function printAsPdf({ rows, subtitle }) { const head = COLUMNS.map((col) => `<th style="text-align:${col.num ? 'right' : 'left'}">${hesc(col.label)}</th>`).join('');
  const body = rows.map((row, i) => { const tds = COLUMNS.map((col) => `<td style="text-align:${col.num ? 'right' : 'left'}">${hesc(row[col.key] ?? '')}</td>`).join('');
    return `<tr><td style="text-align:right;color:#94a3b8">${i + 1}</td>${tds}</tr>`; }).join('');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${REPORT_TITLE}</title>
<style>
  *{box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:16px}
  .rhead{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #1e3a8a;padding-bottom:8px;margin-bottom:6px}
  .rhead h1{font-size:15px;margin:0;color:#1e3a8a}.rhead .meta{font-size:10px;color:#64748b;text-align:right}
  .crit{font-size:10px;color:#475569;margin:6px 0 10px}
  table{width:100%;border-collapse:collapse}
  th{background:#eef2f7;color:#475569;font-size:9px;text-transform:uppercase;padding:5px 6px;border-bottom:1.5px solid #cbd5e1}
  td{font-size:10px;padding:4px 6px;border-bottom:1px solid #eef2f7;white-space:nowrap}
  tr:nth-child(even) td{background:#fafcff}@page{size:A4 landscape;margin:12mm}
</style></head><body>
  <div class="rhead"><h1>${REPORT_TITLE}</h1><div class="meta">${rows.length} demirbaş</div></div>
  <div class="crit"><strong>Kriterler:</strong> ${hesc(subtitle)}</div>
  <table><thead><tr><th style="text-align:right">#</th>${head}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1000); }; }

export function AssetStatusListPage() { usePageMeta(REPORT_TITLE, 'Sabit Rapor');
  const [sp] = useSearchParams();
  const lk = useLookups();

  const params = useMemo(() => { const o = {};
    for (const k of ['vendorId', 'customerId', 'status', 'serialNo', 'serialOp']) { const v = sp.get(k);
      if (v) o[k] = v; }
    return o; }, [sp]);
  const showCriteria = sp.get('showCriteria') === '1';

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Kolon filtre/sıralama/gruplama/sayfalama ReportGrid'in içinde (kalıcı görünüm). Excel/PDF
  // dışa aktarımı kullanıcının GÖRDÜĞÜ kümeyi almalı → grid görünen satırları buraya bildirir.
  // null = grid henüz bildirmedi (Excel ham `rows`'a düşer).
  const [visibleRows, setVisibleRows] = useState(null);
  const [nowLabel] = useState(() => new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  const fetchData = useCallback(async () => { setLoading(true); setError(null);
    try { const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${ENDPOINT}?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rapor alınamadı (${res.status})`);
      const json = await res.json();
      const data = json?.data ?? json;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotals(data?.totals ?? null); } catch (e) { setError(e.message || 'Hata'); setRows([]); } finally { setLoading(false); } }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Grid görünen satırları bildirir (filtre + sıralama uygulanmış); Excel/PDF bunu kullanır.
  const exportRows = visibleRows ?? rows;

  const vendorName = useMemo(() => { const v = lk.vendors.find((o) => String(optId(o)) === String(params.vendorId));
    return v ? optName(v) : (params.vendorId || '—'); }, [lk.vendors, params.vendorId]);
  const customerName = useMemo(() => { const cu = lk.customers.find((o) => String(optId(o)) === String(params.customerId));
    return cu ? optLabel(cu) : params.customerId; }, [lk.customers, params.customerId]);
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === params.status)?.label;
  const summaryText = [`Distribütör: ${vendorName}`, customerName ? `Müşteri: ${customerName}` : null,
    statusLabel ? `Durum: ${statusLabel}` : null, params.serialNo ? `Seri No: ${params.serialNo}` : null].filter(Boolean).join(' · ');

  const onExcel = () => { const cols = COLUMNS.map((col) => ({ field: col.key, headerName: col.label, valueGetter: ({ row }) => row[col.key] }));
    exportToExcel(exportRows, cols, `${REPORT_CODE}_demirbas_son_durum`); };

  return (
    <div style={l.wrap}>
      <div style={l.topbar}>
        <div style={l.topLeft}>
          <span style={l.titleIcon}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.3 7 12 12l8.7-5" /><path d="M12 22V12" /></svg>
          </span>
          <div>
            <div style={l.title}>{REPORT_TITLE}</div>
            <div style={l.subtitle}>{loading ? 'Yükleniyor…' : `${fmtInt(rows.length)} demirbaş kaydı · ${nowLabel}`}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={l.iconBtn} title="PDF İndir" onClick={() => printAsPdf({ rows: exportRows, subtitle: summaryText })}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15h1.5a1.5 1.5 0 0 0 0-3H9v6" /><path d="M15.5 12H14v6" /><path d="M14 15h1.5" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Excel'e Aktar" onClick={onExcel}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16794c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 3 3 3-3" /><path d="M12 12v6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Yazdır" onClick={() => window.print()}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          </button>
        </div>
      </div>

      <div style={l.critBar}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #94a3b8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
        <span style={{ fontWeight: 800, color: 'var(--text-secondary, #64748b)' }}>KRİTERLER</span>
        <span style={l.critChip}>Distribütör: {vendorName}</span>
        {showCriteria && customerName && <span style={l.critChip}>Müşteri: {customerName}</span>}
        {showCriteria && statusLabel && <span style={l.critChip}>Durum: {statusLabel}</span>}
        {showCriteria && params.serialNo && <span style={l.critChip}>Seri No: {params.serialNo}</span>}
      </div>

      {totals && (
        <div style={l.kpiRow}>
          <KpiCard label="Toplam Demirbaş" value={fmtInt(totals.toplam)} bg="rgba(0,115,234,0.10)" fg="var(--accent)"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg>} />
          <KpiCard label="Aktif" value={fmtInt(totals.aktif)} bg="rgba(22,163,74,0.10)" fg="#16a34a"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>} />
          <KpiCard label="Arızalı" value={fmtInt(totals.arizali)} bg="rgba(217,119,6,0.10)" fg="#d97706"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>} />
          <KpiCard label="Pasif" value={fmtInt(totals.pasif)} bg="var(--bg-input, #f1f5f9)" fg="#64748b"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>} />
          <KpiCard label="Hurda" value={fmtInt(totals.hurda)} bg="rgba(220,38,38,0.10)" fg="#dc2626"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>} />
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 20px 20px' }}>
        {error && <div style={{ ...l.empty, color: '#dc2626' }}>{error}</div>}
        {!error && !loading && (
          <ReportGrid
            reportKey="asset-status"
            columns={COLUMNS}
            rows={rows}
            onVisibleRowsChange={setVisibleRows}
          />
        )}
        {loading && <div style={l.empty}>Yükleniyor…</div>}
      </div>
    </div>
  ); }
